<template>
  <div id="CategoryPage">
    <div
      v-if="categoryJson === null && categoryWithoutInchExist"
      class="back-to-all-categories p-8"
    >
      <div> {{ $tv('backToAllCategoriesText', 'categories') }} </div>
      <nuxt-link
        :to="allCategoriesPageUrl"
        class="k-button k-button-md k-button-solid k-button-solid-primary"
      >
        <span>{{ $tv('backToAllCategoriesBtn', 'categories') }} </span>
      </nuxt-link>
    </div>
    <div
      v-if="categoryJson"
      id="descriptionSection"
    >
      <div class="header category-header">
        <div class="category-header-and-button">
          <h1
            class="h1--underline"
            v-text="categoryJson.displayName"
          />
          <div v-if="buttonUrl !== ''">
            <a
              :href="getButtonHref"
              target="_blank"
            >
              <ReactiveButton :button-text="buttonText" />
            </a>
          </div>
        </div>
      </div>
      <CategoryNavigation
        v-if="categoryType !== 'top' && !pageLoading"
        :menu-items="categoryPageNavs"
        :default-menu-item-id="'descriptionSection'"
        :offset="currentOffset"
      />
      <div
        class="category-page__informations"
      >
        <div class="category-page__images-description">
          <div
            class="category-page__category-images"
            v-if="categoryType !== 'between'"
          >
            <div>
              <div>
                <img
                  :src="getPimImgUrlMedium(categoryJson.designSketchImage || categoryJsonFallback?.designSketchImage)"
                  :alt="categoryJson.designSketchImage?.alt"
                  class="designsketch-img"
                >
              </div>
            </div>
          </div>
          <div
            class="category-page__shortened-description"
            ref="categoryDescriptionContainer"
          >
            <h2
              v-if="categoryJson.description"
            >
              {{ $tv('description', 'categories') }}
            </h2>
            <Markdown
              :text="categoryDescriptionTrim"
              class="header__subheadline header__subheadline--element"
              ref="categoryDescription"
              v-if="categoryJson.description"
            />
            <Download
              v-if="categoryDownloads.length"
              :files="categoryDownloads"
            />
          </div>
        </div>
        <div
          class="category-page__tabs"
          v-if="categoryType !== 'top' && !hasService"
        >
          <TabStrip
            v-if="tabs && tabs.length > 0 && !isMobile"
            :tabs="tabs"
            class="category-page__tabstrip-container tabstrip"
            :selected="selected"
            @select="onSelect"
          >
            <template #custom="{props}">
              <Markdown
                :text="props?.dataItem?.data"
                class="header__subheadline"
              />
            </template>
          </TabStrip>
          <ExpansionPanel
            v-else-if="tabs && tabs.length > 0"
            :items="tabs"
          />
        </div>
      </div>
      <CategoryListNew
        :categories="categoryJson.subcategories"
        :category-type="categoryType"
        :path="path"
        :grouped="!categoryJson.subcategories?.some(sc => sc.brands && sc.brands.length > 1)"
        @applyFilters="handleApplyFilter"
        v-show="categoryJson.subcategories"
      />
      <div
        class="service-section"
        v-if="categoryType === 'top' && hasService"
      >
        <ServiceArea
          :content="service.content"
          :title="service.name"
        />
      </div>
      <div
        :class="{ 'filter--mobile': isMobile && openFilter }"
        v-if="categoryType !== 'top'"
      >
        <div
          id="productTable"
          class="filter-container"
          v-if="categoryType !== 'top'"
        >
          <span
            v-show="isMobile && openFilter"
            class="filter-close-icon"
          >
            <a
              href="javascript:void(0)"
              class="close"
              @click="openFilter = false"
            >
              <NavigationClose />
            </a>
          </span>
          <CategoryFilter
            v-if="categoryFilters && ((!(products?.count === 0 && !(hasActiveFilters || showInchAssortmentOnly)) || products?.count > 0) && (products?.facets != null) || resetInProgress || productsLoading)"
            ref="categoryFilter"
            :filters="filters"
            :facets="products?.facets"
            :product-count="products?.count"
            :is-mobile="isMobile"
            :category-type="categoryType"
            :show-on-categories-page="true"
            :popup-class-name="'category-page__dropdown-popup'"
            @applyFilters="handleApplyFilter"
            @filterBuyableProducts="loadProducts"
            @filterInchValues="loadProducts"
            :show-reset-on-ccrs="false"
            :class-name="'category'"
            @update:activeFilter="v => activeFilterShortName = v || ''"
          >
            <div
              class="installationsketch-container"
              v-if="categoryJson.installationSketchImage?.imgURL !== fallBackImg && categoryJson.installationSketchImage?.imgURL"
            >
              <div class="image-title">
                {{ $tv('categories.filters.imageTitle', 'categories') }}
              </div>
              <div
                class="image-container"
                @click="handleImageLinkClick"
              >
                <InstallationSketchSvg
                  v-if="getPimImgUrlMedium(categoryJson.installationSketchImage)?.toLowerCase().endsWith('.svg')"
                  :src="getPimImgUrlMedium(categoryJson.installationSketchImage)"
                  :active-id="activeFilterShortName"
                  class="installationsketch-img"
                />
                <img
                  v-else
                  :src="getPimImgUrlMedium(categoryJson.installationSketchImage)"
                  class="installationsketch-img"
                >
                <div class="image-text-link">
                  {{ $tv('categories.filters.imageTextLink', 'categories') }}
                </div>
              </div>
            </div>
          </CategoryFilter>
          <FilterSkeleton v-else-if="firstLoad" />
        </div>
      </div>
      <div
        class="button-container"
        v-if="isMobile && categoryType !== 'top' && ((filters && filters.length > 0) || loggedIn)"
      >
        <kbutton
          :theme-color="'primary'"
          @click="openFilter = true"
        >
          <span class="subline">
            {{ $tv('filter', 'categories') }}
          </span>
        </kbutton>
        <kbutton
          :disabled="!activeFilters"
          :theme-color="'base'"
          @click="resetFilters"
        >
          <span class="subline">{{ $tv('reset-filter', 'categories') }}</span>
        </kbutton>
      </div>
      <div
        class="product-table"
        :class="{'product-table--logged-in': loggedIn}"
        v-if="categoryType !== 'top'"
      >
        <NoProductsFound
          :products="products"
          :has-active-values="hasActiveFilters || showInchAssortmentOnly"
          :form-data="formData"
          :reset-in-progress="resetInProgress"
          @contact-click="handleContactClick"
          @reset-filters="resetFilters"
          @clear-form-data="clearFormData"
        />
        <ProductTable
          v-if="columnsJson && products?.count > 0 && !firstLoad"
          :is-loading="productsLoading"
          :columns="columnsJson"
          :products="getMappedProductsLocal(products)"
          :sort="tableOptions.sort"
          :pagination="tableOptions.pagination"
          :total="products?.count || 0"
          :prices="prices"
          :loading-indicators="loadingIndicators"
          :availabilities="availabilities"
          :successor-availabilities="successorAvailabilities"
          :show-design-sketch="categoryJson.subcategories && categoryJson.subcategories.length > 0"
          :is-default-customer="isDefaultCustomer"
          :is-mobile="isMobile"
          :popup-class-name="'category-page__dropdown-popup'"
          @favoriteItem="setItemAsFavorite"
          @shopItem="addItemToBasket"
          @sortByColumn="sortItems"
          @changePage="changePageLocal"
          @filterInchValues="loadProducts"
          @calculate="handleCalculate"
          ref="productTable"
        />
        <ProductTableSkeleton v-show="firstLoad" />
        <div
          class="service-section"
          v-if="categoryType === 'between' && hasService"
        >
          <ServiceArea
            :content="service.content"
            :title="service.name"
            :css-class="'between'"
          />
        </div>
        <div
          v-show="categoryJson?.SEO?.content"
          class="seo-content"
        >
          <Markdown :text="categoryJson?.SEO?.content" />
        </div>
      </div>
      <PopupBase
        :show="openCartModal"
        @close-pop-up="closeCartModal"
        :css-class="buyboxClass"
        v-if="categoryType !== 'top'"
      >
        <ProductBuyBox
          :product-data="product"
          :product="product"
          :is-default-customer="isDefaultCustomer"
          :loading="reloadProductsInProgress"
          @productSuccessfullyAdded="onProductSuccessfullyAdded"
          @reloadProduct="reloadProduct"
          class="buy-box-in-popup"
        />
      </PopupBase>
      <PopupBase
        :show="showMoreDetailModal"
        @close-pop-up="closeMoreDetailModal"
        v-if="categoryType === 'design'"
      >
        <div class="installation-detail-view">
          <div class="headline">
            <Headline
              :headline="$tv('category.image.popupTitle', 'categoryImagePopup')"
              class="image-headline"
            />
          </div>
          <div class="installation-detail-view__image-container">
            <div v-if="categoryJson.installationSketchImage?.imgURL !== fallBackImg && categoryJson.installationSketchImage?.imgURL">
              <InstallationSketchSvg
                v-if="getPimImgUrlMedium(categoryJson.installationSketchImage)?.toLowerCase().endsWith('.svg')"
                :src="getPimImgUrlMedium(categoryJson.installationSketchImage)"
                :active-id="activeFilterShortName || ''"
                class="installationsketch-img"
              />
              <img
                v-else
                :src="getPimImgUrlMedium(categoryJson.installationSketchImage)"
                :alt="categoryJson.installationSketchImage?.alt"
                class="installationsketch-img"
              >
            </div>
            <div class="popup-switch">
              <ToggleSwitchSquare
                v-if="showInchToggle"
                :is-checked="showInchValues"
                :label-left="$tv('metricInchToggleLeftText','product')"
                :label-right="$tv('metricInchToggleRightText','product')"
                @change-value="toggleShowInchValues"
              />
            </div>
          </div>
          <div class="installation-detail-view__filter-container">
            <CategoryFilter
              v-if="categoryFilters && ((!(products?.count === 0 && !(hasActiveFilters || showInchAssortmentOnly)) || products?.count > 0) && (products?.facets != null) || resetInProgress || productsLoading)"
              ref="categoryFilter"
              :filters="filters"
              :facets="products?.facets"
              :product-count="products?.count"
              :is-mobile="isMobile"
              :in-popup="true"
              @applyFilters="handleApplyFilter"
              @filterBuyableProducts="loadProducts"
              @filterInchValues="loadProducts"
              :show-reset-on-ccrs="false"
              @update:activeFilter="v => activeFilterShortName = v || ''"
            />
          </div>
        </div>
      </PopupBase>
    </div>
  </div>
</template>

<script setup>
import ProductBuyBox from '~/components/product-detail/ProductBuyBox.vue'
import {useProductService} from '~/composables/product_service'
import {
  cleanupFacets,
  cleanupFilters,
  SHOW_INCH_VALUES_FILTER
} from '~/assets/js/filters'
import {
  CATEGORIES,
  NON_ATTRIBUTE_FILTERS,
  PROPERTY_SCOPE_NAMES,
  FALLBACK_IMAGE,
  FORM_DEFAULT_DATA,
  URL_HTTP_REGEXP_CHECK,
  FACET_VALUE_TYPES,
  REGION_KEYS,
  MOBILE_BREAKPOINT
} from '~/assets/js/constants'
import {storeToRefs} from 'pinia'
import {TabStrip} from '@progress/kendo-vue-layout'
import FilterSkeleton from '~/components/skeletons/FilterSkeleton.vue'
import ProductTableSkeleton from '~/components/skeletons/ProductTableSkeleton.vue'
import {getMappedProductNew, getMappedProducts} from '~/assets/js/utils/product_attributes_mapping'
import NavigationClose from '~/assets/icons/navigation_close.svg?component'
import {useUserStore} from '@/store/user'
import {useCartStore} from '@/store/cart'
import {processFiltersWithDuplicateDetection} from '~/utils/filterDuplicateDetection'
import ProductTable from '@/components/product-detail/ProductTable'
import ReactiveButton from '~/components/ReactiveButton'
import Download from '~/components/Download'
import CategoryFilter from '~/components/category/CategoryFilter'
import CategoryListNew from '~/components/category/CategoryListNew'
import ServiceArea from '~/components/category/ServiceArea.vue'
import PopupBase from '~/components/popup/PopupBase'
import CategoryNavigation from '~/components/category/CategoryNavigation'
import useBreadcrumb from '@/composables/useBreadcrumb'
import useI18nExtended from '~/composables/usei18nExtended'
import NoProductsFound from '~/components/category/NoProductsFound.vue'
import {useDefaultCustomer} from '~/composables/default_customer'
import {useProductAvailabilityPricesHelper} from '~/composables/product_availabilities_prices_helper'
import {useReloadProduct} from '~/composables/reload_product'
import {useCategoryFilters} from '~/composables/category_filters'
import {useCanonicalPathHelper} from '~/composables/canonical_path_helper'
import createSeoMetaInfo from '~/assets/js/opengraph/helper'
import usePim from '~/composables/pim_json_loader'
import {usePimHelper} from '~/composables/pim_helper'
import {getPages} from '~/config/helper'
import ToggleSwitchSquare from '~/components/input-components/ToggleSwitchSquare.vue'
import Headline from '~/components/product-detail/bb_components/HeadLine.vue'
import InstallationSketchSvg from '~/components/category/InstallationSketchSvg.vue'
import ExpansionPanel from '~/components/ExpansionPanel.vue'
import {useGtm} from '@gtm-support/vue-gtm'
import {useEventItemConverter} from '@/composables/useEventItemConverter'
import {useDynamicEvents} from '~/composables/useDynamicEvents'
import {useCategoriesMenuStore} from '@/store/categories-menu'

const route = useRoute()

const segments = route.path.split('/').slice(0, 4)
const currentRegion = segments.length > 1 ? segments[1] : ''

// Helper function to load category data with usePim fallback
const loadCategoryWithPim = async () => {
  let legacyPath = ''
  try {
    legacyPath = route.path

    if (route.params.categorypath.length > 1) {
      const pages = getPages()
      const currentLocale = i18n.locale.value
      let products = pages[currentLocale]?.products || ''
      let productPath = false
      route.params.categorypath.forEach((segment, index) => {
        if (segment === products) {
          productPath = true
          return
        }
        if (productPath && index === route.params.categorypath.length - 1) {
          navigateTo(`/${currentRegion}/${currentLocale}/${products}/${segment}`, {redirectCode: 301})
          return

        }
        if (index < route.params.categorypath.length - 1) {
          legacyPath = legacyPath.replace(`/${segment}`, '')
        }
      })
      if (!legacyPath.endsWith('.svg')) {
        const categoryFileName = `v1${legacyPath}.json`
        const result = await usePim(categoryFileName)

        if (result.url) {
          navigateTo(result.url, {redirectCode: 301})
        }
      }
    }

    if (legacyPath.endsWith('.svg')) {
      return null
    }
    const categoryFileName = `v1${legacyPath}.json`
    const result = await usePim(categoryFileName)

    if (result?.redirect) {
      navigateTo(result.redirect, {redirectCode: 301})
    }

    return result
  } catch (error) {
    return null
  }
}

const productsLoading = ref(false)
const selected = ref(0)
const service = ref({name: '', content: ''})
const hasService = ref(false)
const tabs = ref([])
const onMobile = ref(false)

const params = route.params

const i18n = useI18nExtended()
const productService = useProductService()
const nuxtApp = useNuxtApp()
const pageLoading = ref(false)
nuxtApp.hook('page:start', () => { pageLoading.value = true })
nuxtApp.hook('page:finish', () => { pageLoading.value = false })
const {isDefaultCustomer} = useDefaultCustomer()
const {adjustCanonicalPath} = useCanonicalPathHelper()
const {showInchValues} = storeToRefs(useUserStore())
const {setShowInchValues} = useUserStore()
const {buildDynamicFilterProps} = useDynamicEvents()

// product related
const product = ref(null)
const products = ref(null)
const {reloadProduct, reloadProductsInProgress} = useReloadProduct(product, i18n.locale, nuxtApp.$globalization)
const {prices, availabilities, successorAvailabilities, loadingIndicators, loadPrices} = useProductAvailabilityPricesHelper(products)

// category related
const categories = ref([])
const categoryFilter = ref(null)

let currentLevel
currentLevel = params.categorypath[params.categorypath.length - 1]

// Load category data using usePim
let categoryJson = null
let categoryJsonFallback = null

// Try to load category JSON file using usePim
categoryJson = await loadCategoryWithPim()

const defaultLang = i18n.defaultLocale || 'en'

const {mapCategoryFilter, mapCategoryTableColumns, getPimImgUrlMedium} = usePimHelper()

const columnsJson = computed(() => {
  return mapCategoryTableColumns(categoryJson)
})

const categoryType = computed(() => {
  return categoryJson?.type || ''
})

const loggedIn = computed(() => {
  return nuxtApp.$auth.loggedIn
})

const currentOffset = computed(() => {
  if (isMobile.value && loggedIn.value) {
    return 120
  }

  if (isMobile.value && !loggedIn.value) {
    return 105
  }

  return 150
})

if (defaultLang !== i18n.locale.value) {
  let url_alternatives = categoryJson?.url_alternatives
  let url_alternativesRegion = url_alternatives?.[currentRegion.toLowerCase()]
  let url_alternativesLang = url_alternativesRegion?.[defaultLang]

  if (url_alternativesLang && !url_alternativesLang.includes('#name?') && !url_alternativesLang.includes('undefined')) {
    let fallbackJsonPath = 'v1' + url_alternativesLang + '.json'
    categoryJsonFallback = await usePim(fallbackJsonPath)
  }
}

let currentCustomerUuid

let formData = FORM_DEFAULT_DATA.CONTACT_FORM
let path = ''
let facets = null
// let currentCategory = null
const openCartModal = ref(false)
const showMoreDetailModal = ref(false)
const activeFilterShortName = ref('')
// let loggedInStatus = true
let categoryTrigger = ref(0)
let firstLoad = true
let openFilter = false
let showInchAssortmentOnly = false
let fallBackImg = FALLBACK_IMAGE
let productDebouncer = null

productsLoading.value = true // from fetch

const loadProducts = async () => {
  if (categoryJson?.type === 'top') {
    productsLoading.value = false
    if (firstLoad) {
      firstLoad = false
    }
    return
  }
  if (productDebouncer) {
    clearTimeout(productDebouncer)
  }
  productsLoading.value = true
  productDebouncer = setTimeout(async () => {
    if (process.client) {
      categoryFilters = [
        ...(categoryJson?.categoryAttributes_filter_core?.map(f => mapCategoryFilter(f, true)) || []),
        ...(categoryJson?.categoryAttributes_filter?.map(f => mapCategoryFilter(f, false)) || [])
      ]
      if (!facets && Array.isArray(categoryFilters)) {
        facets = categoryFilters.filter(
          cat => cat.Attribute.IndexPath !== null
        && cat.Attribute.IndexPath !== ''
        && cat.Attribute.IsFilterable
        && FACET_VALUE_TYPES.includes(cat.Attribute.ValueType)
        && cat.Attribute.TechnicalName.toLowerCase() !== NON_ATTRIBUTE_FILTERS.CATALOG_FLAG.toLowerCase())
          .map(cat => cat.Attribute.IndexPath)
        facets = cleanupFacets(facets)
      }
      if (categoryJson && (categoryJson.designTechnicalNames || categoryJson.technicalName)) {
        const enhanceQueryFilters = [...filters.value]

        let designs = []
        if (categoryJson.subcategories && categoryJson.subcategories.length > 0) {
          designs = showInchAssortmentOnly ? categoryJson.subcategories.filter(d => d.hasInchProducts).map(sc => sc.technicalName) : categoryJson.designTechnicalNames
        } else {
          designs = categoryJson.designTechnicalNames
        }
        designs = designs.map(d => ({TechnicalName: d}))
        products.value = await productService.getProductsForDesignsNew(
          designs,
          facets,
          nuxtApp.$globalization.getRegion(),
          i18n.locale.value,
          enhanceQueryFilters,
          tableOptions.value,
          showInchValues.value ? [SHOW_INCH_VALUES_FILTER.customQuery] : null,
          PROPERTY_SCOPE_NAMES.CATEGORY_PAGE_NEW,
          categoryAttributeSelects,
          showInchAssortmentOnly
        )
        if (products.value && !products.value.facets) {
          products.value.facets = []
        }
      }
      productsLoading.value = false
      if (firstLoad) {
        firstLoad = false
      }
      if (resetInProgress.value) {
        resetInProgress.value = false
      }
    }
  }, 100)
}

const handleCalculate = async () => {
  await Promise.all([
    loadPrices()
  ])
}

const {tableOptions, isMobile, changePage, sortItems, filters, filter, hasActiveFilters, resetInProgress, resetFilters} = useCategoryFilters(() => loadProducts(), categoryFilter)

const changePageLocal = (event) => {
  changePage(event)
}

const closeCartModal = () => {
  openCartModal.value = false
}

const closeMoreDetailModal = () => {
  showMoreDetailModal.value = false
}

selected.value = 0

let technicalName = null

let categoryFilters, categoryAttributeSelects
let categoryWithoutInchExist = false
if (!categoryJson) {
  throw createError({
    statusCode: 404,
    statusMessage: 'Category not found name: ' + currentLevel + ', route: ' + route.fullPath,
    show: false,
    fatal: true
  })
} else {
  categoryFilters = [
    ...(categoryJson?.categoryAttributes_filter_core?.map(f => mapCategoryFilter(f, true)) || []),
    ...(categoryJson?.categoryAttributes_filter?.map(f => mapCategoryFilter(f, false)) || [])
  ]
  //TODO PIM #41173 load Category filters from JSON file, map into old structure, clarify how to set the flags
  if (categoryJson) {
    categoryAttributeSelects = categoryFilters.map(f => f.IndexPath)
  }

  filters.value = cleanupFilters(categoryFilters)

  // Check for and remove duplicate filters using utility function
  filters.value = processFiltersWithDuplicateDetection(filters.value, {
    location: 'category-page',
    route: route.path,
    category: categoryJson?.technicalName
  })
}

//TODO PIM #41173 load Category filters from JSON file, not sure if needed anymore, since we already have translated urls
let translatedParams = {}
for (let localeCode of i18n.localeCodes.value) {
  if (localeCode === i18n.locale.value) {
    continue
  }
  let localizedUrl = categoryJson.url_alternatives?.[currentRegion.toLowerCase()]?.[localeCode]?.split('/')?.at(-1) || ''
  if (!Array.isArray(translatedParams[localeCode]?.categorypath)) {
    translatedParams[localeCode] = {categorypath: []}
  }
  translatedParams[localeCode].categorypath.push(localizedUrl)
}
route.meta.nuxtI18n = translatedParams

if (categoryJson?.contents?.filter(c => c.type === 'text').length > 0) {
  const customContents = categoryJson.contents.filter(c => c.type === 'text')
  hasService.value = customContents.some(c => c.name.toLowerCase() === 'service')
  service.value = customContents.find(c => c.name.toLowerCase() === 'service')

  customContents.forEach(content => {
    tabs.value.push({
      title: content.name,
      content: 'custom',
      data: content.content,
      id: content.name.toLowerCase().replace(/\s/g, '-')
    })
  })
}

const categoryPageNavs = computed(() => {
  const navs = []
  if (categoryJson?.description) {
    navs.push({id: 'descriptionSection', label: i18n.tv('description', 'categories')})
  }
  if (categoryJson?.subcategories.length && categoryType.value !== 'top') {
    navs.push({id: 'designsSection', label: i18n.tv('designs', 'categories')})
  }
  if (categoryFilters?.length) {
    navs.push({id: 'productTable', label: i18n.tv('items', 'cart')})
  }
  if (hasService.value) {
    navs.push({id: 'serviceSection', label: i18n.tv('service', 'categories')})
  }
  return navs
})

// TODO readd CustomTabs

// head
const router = useRouter()
const routePath = route.path
let i18nHead = useLocaleHead({addSeoAttributes: true, route: route, router: router, i18n: i18n}).value
adjustCanonicalPath(routePath, i18nHead)

const defaultTitle = `${(categoryJson && categoryJson.name) || 'Categories'} | Freudenberg FST`

let description = categoryJson?.SEO && categoryJson?.SEO?.meta_description ? categoryJson.SEO.meta_description : i18n.tv({messageKey: 'categoryDescription', messageValues: {name: categoryJson?.name || 'Categories'}}, 'seo')

const headSeoTitle = categoryJson?.SEO?.meta_title
  ? `${categoryJson.SEO.meta_title} | Freudenberg FST`
  : defaultTitle

const head = {
  title: headSeoTitle,
  categoryLanguage: i18n.locale,
  htmlAttrs: {
    ...i18nHead.htmlAttrs
  },
  meta: [
    ...i18nHead.meta,
    {
      hid: 'description',
      name: 'description',
      content: description
    }
  ],
  link: [...i18nHead.link]
}

useHead(head)

const seoInfo = createSeoMetaInfo({
  title: headSeoTitle,
  description: description,
  image: categoryJson.SEO?.og_image !== 'nicht definiert' ? categoryJson.SEO?.og_image : categoryJson?.designSketchImage?.imgURL,
  url: `${useRuntimeConfig().public.BASE_URL}${route.path}`,
  locale: i18n.locale.value
})

useSeoMeta(seoInfo)

let breadcrumbArray = []
breadcrumbArray.push({label: i18n.te('breadcrumbs.category', i18n.locale.value) ? i18n.tv('category', 'breadcrumbs') : 'category', path: `${CATEGORIES.RootCategoriesUrl}`})
breadcrumbArray = breadcrumbArray
  .concat(categoryJson?.breadcrumb?.map(breadcrumb => {
    return {
      label: breadcrumb.name,
      path: breadcrumb.url
    }}))
useBreadcrumb(breadcrumbArray)

const {loadedCustomerUuid} = storeToRefs(useUserStore())
const {deliveryAddressUuid} = storeToRefs(useCartStore())

categories.value = technicalName
currentCustomerUuid = loadedCustomerUuid

onMounted(async () => {
  triggerComputedProp()
  loadProducts()
  trackCategoryPageView()
  nuxtApp.$eventbus.emit('category-type-change', categoryType.value)

  if (typeof window !== 'undefined') {
    onResize()
    window.addEventListener('resize', onResize, {passive: true})
  }
})

onUnmounted(() => {
  if (typeof window !== 'undefined') {
    window.removeEventListener('resize', onResize)
  }
})

const pathCategoryPath = route.params.categorypath
path = `${CATEGORIES.RootCategoriesUrl}/` + pathCategoryPath.join('/')

const getMappedProductsLocal = (products) => {
  return getMappedProducts(products)
}

const setItemAsFavorite = (e) => {
  // eslint-disable-next-line no-console
  console.log('setItemAsFavorite', e)
}

const addItemToBasket = (e, productLocal) => {
  product.value = getMappedProductNew(productLocal)
  openCartModal.value = true
}

const onProductSuccessfullyAdded = () => {
  openCartModal.value = false
}

const handleImageLinkClick = () => {
  showMoreDetailModal.value = true
}

const onSelect = (event) => {
  return (selected.value = event.selected)
}

const triggerComputedProp = () => {
  if (categoryJson && typeof (window) !== 'undefined') {
    const markdownContainer = window?.document?.getElementsByClassName('header__subheadline--element')
    if (!markdownContainer) {
      setTimeout(triggerComputedProp, 100)
    } else {
      categoryTrigger.value++
    }
  }
}

const handleApplyFilter = (filtersArg) => {
  filter(filtersArg)
  openFilter = false
  if (showMoreDetailModal.value) {
    closeMoreDetailModal()
  }
  setTimeout(() => {
    trackSelectedFilterParameters(filters.value)
  })
}

const trackSelectedFilterParameters = (appliedFilters) => {
  const activeFilters = appliedFilters.filter(f => f.isActive)
  const props = activeFilters.length ? buildDynamicFilterProps(activeFilters, showInchValues.value, i18n.tv) : {}

  const ga4payload = useEventItemConverter().convert({
    event: 'category_page_applyfilters',
    add_personalization: true,
    props
  })
  useGtm()?.trackEvent(ga4payload)
}

const categoriesMenuStore = useCategoriesMenuStore()

const trackCategoryPageView = async () => {
  // last breadcrumb entry is the current category itself, so it's excluded from the ancestors
  const ancestors = (categoryJson?.breadcrumb || []).slice(0, -1)

  await categoriesMenuStore.loadMenu(nuxtApp.$globalization.getRegion().toLocaleLowerCase(), i18n.locale.value)
  // breadcrumb entries only expose "name" (no technicalName), so match the menu tree by url instead
  const getTechnicalName = (ancestor) => categoriesMenuStore.findByUrl(ancestor?.url)?.technicalName || ancestor?.name || ''

  const ga4payload = useEventItemConverter().convert({
    event: 'category_page_view',
    add_personalization: true,
    items: [{
      item_id: '',
      item_name: '',
      item_brand: '',
      item_category: categoryJson?.technicalName || '',
      item_category2: getTechnicalName(ancestors[ancestors.length - 1]),
      item_category3: getTechnicalName(ancestors[ancestors.length - 2]),
      item_category4: '',
      item_category5: '',
      item_variant: '',
      location_id: ''
    }]
  })
  useGtm()?.trackEvent(ga4payload)
}

const clearFormData = () => {
  formData = FORM_DEFAULT_DATA.CONTACT_FORM
}

const buyboxClass = computed(() => {
  return 'popup__buy-box-new'
})

const categoryDescriptionTrim = computed(() => {
  return categoryJson?.description?.trim() ?? ''
})

const categoryDownloads = computed(() => {
  let result = []
  if (categoryJson?.contents && categoryJson.contents.filter(c => c.type === 'download').length > 0) {
    const pimBaseUrl = useRuntimeConfig().public.pimDataBaseUrl || ''
    result = categoryJson.contents.filter(c => c.type === 'download').map(
      (categoryAsset) => {
        let downloadUrl = categoryAsset.url
        if (downloadUrl && !downloadUrl.startsWith('http://') && !downloadUrl.startsWith('https://')) {
          downloadUrl = pimBaseUrl + (downloadUrl.startsWith('/') ? '' : '/') + downloadUrl
        }

        return {
          assetType: categoryAsset.type,
          lookupKey: downloadUrl,
          altText: categoryAsset.label,
          label: categoryAsset.label,
          sourceCMS: true
        }
      }
    )
  }
  return result
})

const activeFilters = computed(() => {
  const active = filters.value.filter(f => f.isActive)
  return active && Array.isArray(active) && active.length > 0
})

const buttonUrl = computed(() => {
  return categoryJson?.linkURL ?? ''
})

const buttonText = computed(() => {
  return categoryJson?.linkText ?? ''
})

const getButtonHref = computed(() => {
  const url = buttonUrl.value
  if (URL_HTTP_REGEXP_CHECK.test(url)) {
    return url
  }
  return nuxtApp.$globalization.localePath(url)
})

const allCategoriesPageUrl = computed(() => {
  return nuxtApp.$globalization.localePath(CATEGORIES.RootCategoriesUrl)
})

const showInchToggle = computed(() => {
  return nuxtApp.$globalization.__currentRegion === REGION_KEYS.NA
})

const toggleShowInchValues = () => {
  setShowInchValues(!showInchValues.value)
}

const onResize = () => {
  if (typeof window !== 'undefined') {
    onMobile.value = window.innerWidth < MOBILE_BREAKPOINT
  }
}

watch(deliveryAddressUuid, () => {
  loadProducts()
})

watch(currentCustomerUuid, () => {
  loadProducts()
})

watch(categoryType, (newType) => {
  nuxtApp.$eventbus.emit('category-type-change', newType)
})

</script>

<style lang="scss">
.not-found {
  .header__subheadline {
    font-weight: $base-text-font-weight-light;
  }
}

@include breakpoint-down($lg) {
  .category-page__images-description {
    flex-direction: column;
  }
}

@include breakpoint-up($lg) {
  .category-page__shortened-description {
    max-width: 45rem;

    .category-page__show-more-button {
      position: absolute;
      bottom: 0;
    }
  }
}

.category-header {
  padding: .2rem $default-margin-padding-2x 1rem;

  @include breakpoint-down($md) {
    padding: $default-margin-padding-width;
  }
}

.category-page {
  &__informations,
  &__tabstrip-container {
    background-color: $color__content--background__secondary;
  }

  &__tabs {
    @include breakpoint-down($md) {
      padding: $default-margin-padding-width;
      background-color: $color__white;
    }
  }

  &__images-description {
    display: flex;
    padding: $default-margin-padding-2x;
    gap: $default-margin-padding-width;

    @include breakpoint-down($md) {
      padding: $default-margin-padding-width;
    }
  }

  &__downloads {
    font-size: 1.2rem;
    padding-top: 1rem;

    .downloads__element--text {
      margin-left: 0;
      padding: .5em;
    }
  }

  &__category-images {
    img {
      display: block;
      width: 100%;
      max-height: 9.8125rem;

      &.designsketch-img {
        max-width: 9.8125rem;
      }

      &.installationsketch-img {
        max-width: 15.5625rem;
      }
    }

    >div {
      display: flex;
      justify-content: flex-start;

      >div {
        display: inline-block;
        background-color: $color__white;
        min-height: 9.8125rem;
        min-width: 9.8125rem;
      }
    }
  }

  &__shortened-description {
    display: flex;
    flex: 1 0;
    flex-direction: column;
    overflow: hidden;
    position: relative;

    h2 {
      margin-top: .5rem;
      margin-bottom: .5rem;
    }

    p {
      margin-top: 0;
    }

    .header__subheadline {
      transition: all $transition-time__default ease-in-out;
      overflow: hidden;
      position: relative;
      display: block;
      -webkit-box-orient: vertical;

      ul, ol {
        display: block;
        margin-block-start: 0.25rem;
      }
    }

    .download {
      display: flex;
      gap: $default-margin-padding-width * 1.5;
    }
  }

  &__tabstrip-container {
    margin-top: $default-margin-padding-width;

    @include breakpoint-up($md) {
      margin: 0 $default-margin-padding-2x;
      padding-bottom: 1rem;
    }

    .k-content .k-animation-container {
      display: block;
    }

    .k-tabstrip-items {
      .k-link {
        padding: .625rem 4.375rem;
        font-size: map-get($base-text-button, font-size);
        font-weight: map-get($base-text-button, font-weight);
        background-color: $color__content--background__primary;

        @include breakpoint-down($md) {
          padding: .5rem 2rem;
        }
      }

      .k-item {
        margin-right: .5rem;
        padding-bottom: .25rem;
      }
    }
  }
}

.service-section {
  background-color: $color__content--background__secondary;
}

[id='CategoryPage'] {
  padding-bottom: 0;

  > :has(.product-table) {
    .product-table {
      padding-bottom: 11.5rem;
    }

    @include breakpoint-down($md) {
      .product-table {
        padding-bottom: 0;

        .product-table__no-products-found{
          margin-top: $default-margin-padding-width;
        }
      }
    }
  }

  > :not(:has(.product-table)) {
    .service-section {
      padding-bottom: 12.5rem;
    }

    @include breakpoint-down($md) {
      .service-section {
        padding-bottom: $default-margin-padding-width * 2.25;

        .service-area {
          padding-bottom: 0;
        }
      }
    }
  }

  > :not(:has(.product-table, .service-section)) {
    .category-list {
      padding-bottom: 11.25rem;
    }

    @include breakpoint-down($md) {
      .category-list {
        padding-bottom: $default-margin-padding-width * 1.5;
      }
    }
  }

  h1 {
    margin-top: 0;
    margin-bottom: 1rem;
  }

  .category-header-and-button {
    display: flex;
    flex-direction: row;
    justify-content: space-between;

    a {
      color: transparent;
    }
  }

  @include breakpoint-down($md) {
    .category-header-and-button {
      flex-direction: column;
      justify-content: unset;

      >div {
        margin-top: 1rem;
      }
    }
  }

  .header__subheadline {
    font-weight: $base-text-font-weight-light;
    font-size: $base-text-font-size;
    line-height: 1.1375rem;

    ul {
      li {
        list-style-type: disc;
      }
    }
  }

  .applications-headline {
    font-weight: $base-text-font-weight-bold;
    color: $color__primary;
  }

  .header__applications {
    padding-top: 1rem;
    line-height: 2rem;
    display: flex;
    flex-wrap: wrap;

    h2 {
      width: 100%;
      margin-bottom: 1rem;
    }

    &__item {
      padding: .3rem;
      margin: .1rem;
      background-color: $color__content--background__primary;
      border: .05rem $color--input__border__secondary--hover solid;
      border-radius: .2rem;
      box-shadow: 0 .02rem .02rem $color--card__shadow;
    }
  }

  .button-container {
    display: flex;
    justify-content: space-between;
    flex-flow: row wrap;
    margin: $default-margin-padding-2x .9375rem 0 .9375rem;

    .k-button {
      margin-bottom: $default-margin-padding-width;
    }

    @include breakpoint-down($md) {
      display: none;
    }
  }

  .product-table {
    position: relative;
    min-height: 15.625rem;
    padding-top: .5rem;

    .skeleton__table {
      margin-left: 1rem;
      margin-right: 1rem;
    }

    &--logged-in {
      @include breakpoint-down($md) {
        .pagination-controls .pagination-size-dropdown {
          bottom: -1.425rem;
        }
      }

      @include breakpoint-down(26.875rem) {
        .pagination-controls .pagination-size-dropdown {
          bottom: 2.25rem;
          width: 100%;
        }

        .k-grid-pager {
          margin-top: 2.595rem;
          justify-content: flex-start;
        }

        .pagination-controls.experimental-position {
          width: calc(100% - .2rem);
        }
      }
    }

    &:not(&--logged-in) {
      @include breakpoint-down(26.875rem) {
        .pagination-controls .pagination-size-dropdown {
          bottom: 4rem;
          width: 100%;
        }

        .k-grid-pager {
          margin-top: 2.595rem;
          justify-content: flex-start;
        }

        .pagination-controls.experimental-position {
          width: calc(100% - .2rem);
        }
      }
    }

    @include breakpoint-up($md) {
      background-color: $color__content--background__secondary;
      padding-top: $default-margin-padding-2x;

      .skeleton__table {
        margin-left: 2rem;
        margin-right: 2rem;
      }
    }

    .product-table__no-products-found__content {
      margin-bottom: 2rem;
    }
  }

  .seo-content {
    padding: $default-margin-padding-2x;
  }

  @include breakpoint-down($md) {
    .seo-content {
      padding: $default-margin-padding-width;
    }
  }

  .footer {
    padding: 4rem $default-margin-padding-2x;

    &__content {
      &__subheader {
        color: $color__primary;
        text-transform: uppercase;
        margin-bottom: 1em;
      }

      &__header {
        font-size: 2em;
        margin-bottom: 1em;
      }

      &__content {
        line-height: 1.25em;
        font-weight: lighter;
        white-space: pre-line;
      }
    }

    &__image {
      display: block;
      margin-top: 2.5rem;
      margin-left: $default-margin-padding-width;

      @include breakpoint-down($md) {
        margin-left: 0;
      }
    }
  }

  .back-to-all-categories .k-button {
    margin-top: 1rem;
  }
}

.installation-detail-view {
  display: flex;
  flex-direction: column;

  @include breakpoint-down($md) {
    .filter-item-wrapper {
      margin-left: 0;
      width: auto;
    }

    .category-filter-container__filters__wrapper__content__filter {
      width: 100%;
    }

    .category-filter-container__button__wrapper > button {
      width: 100%;
    }

    .category-filter-container__label-container {
      padding: 0;
    }
  }

  &__image-container {
    display: flex;
    justify-content: center;
    width: 100%;
    margin-bottom: $default-margin-padding-width;

    .popup-switch {
      position: absolute;
      right: 1rem;
    }
  }

  .category-filter-container h2, .category-filter__unit-switch {
    display: none;
  }

  .category-filter-container {
    background-color: unset;
    border-top: .125rem solid $color__content--background__secondary;
    padding: 0;

    &__button-container .k-button {
      margin-bottom: 0;
    }

    .category-filter-header-wrapper {
      margin-top: $default-margin-padding-2x;
    }
  }
}

.filter-close-icon {
  position: absolute;
  right: .625rem;
  top: .625rem;
  font-size: 1.5625rem;
  z-index: 999;

  .close {
    color: $color__black;
  }
}

body:has(.filter--mobile) {
  overflow: hidden;
}

.k-animation-container {
  z-index: 999 !important;

  &:has(.k-popup.category-page__dropdown-popup) {
    z-index: 10 !important;
  }
}
</style>
